/**
 * Cycle hooks & shared helpers — operational coaching primitive.
 *
 * Cycle is trainer-authored context: focus, week count, deload pairing.
 * NOT an analytics derivation, NOT auto-created. See backend
 * /app/backend/models/cycle.py.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FOCUS_LABELS = {
  hypertrophy: 'Hypertrophy',
  strength: 'Strength',
  maintenance: 'Maintenance',
  rehab: 'Rehab',
  deload: 'Deload',
  general: 'General',
};

export const FOCUS_OPTIONS = Object.keys(FOCUS_LABELS);

/**
 * Days between an ISO date and today (UTC). Returns null if date is missing
 * or unparseable. Negative values mean "in the future" — caller decides
 * whether to render.
 */
export const daysSince = (isoDate) => {
  if (!isoDate) return null;
  try {
    const d = new Date(isoDate + (isoDate.length === 10 ? 'T00:00:00Z' : ''));
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

/**
 * Compact "Week N" or "Week N/M" string for the cycle context block.
 * Uses inclusive week counting (start week = Week 1).
 *
 * Overrun handling: when elapsed weeks exceed `expected_weeks`, we render
 * `Week M+/M` (e.g. `Week 8+/8`) rather than the misleading literal
 * `Week 16/8`. Keeps the operational tone calm — the trainer just sees
 * the cycle has run long without an alarming number.
 */
export const formatWeekN = (cycle) => {
  if (!cycle?.start_date) return null;
  const days = daysSince(cycle.start_date);
  if (days == null || days < 0) return 'Week 1';
  const week = Math.floor(days / 7) + 1;
  const expected = cycle.expected_weeks;
  if (expected && expected > 0) {
    if (week > expected) return `Week ${expected}+/${expected}`;
    return `Week ${week}/${expected}`;
  }
  return `Week ${week}`;
};

/**
 * Hook: load + manage the single active cycle for a client.
 * Returns { cycle, loading, refresh, create, close, patch }.
 *
 * `create` auto-closes any prior active cycle on the backend.
 */
export const useCurrentCycle = (clientId) => {
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(!!clientId);

  const refresh = useCallback(async () => {
    if (!clientId) {
      setCycle(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cycles/current/${clientId}`);
      setCycle(r.data || null);
    } catch (e) {
      console.error('[useCurrentCycle] failed', e);
      setCycle(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(async (payload) => {
    const r = await axios.post(`${API}/cycles`, { client_id: clientId, ...payload });
    setCycle(r.data);
    return r.data;
  }, [clientId]);

  const close = useCallback(async (payload) => {
    if (!cycle?.id) return null;
    const r = await axios.post(`${API}/cycles/${cycle.id}/close`, payload || {});
    setCycle(null);
    return r.data;
  }, [cycle?.id]);

  const patch = useCallback(async (updates) => {
    if (!cycle?.id) return null;
    const r = await axios.patch(`${API}/cycles/${cycle.id}`, updates);
    setCycle(r.data);
    return r.data;
  }, [cycle?.id]);

  return { cycle, loading, refresh, create, close, patch };
};
